<template>
  <Transition name="slide-down">
    <div v-if="isOffline" class="offline-banner">
      <div class="offline-banner__container">
        <div class="offline-banner__content">
          <!-- Иконка -->
          <div class="offline-banner__icon">
            <svg
              v-if="isReconnecting"
              class="offline-banner__spinner"
              width="20"
              height="20"
              viewBox="0 0 50 50"
            >
              <circle
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="currentColor"
                stroke-width="4"
              />
            </svg>
            <Icon v-else name="heroicons:wifi" class="offline-banner__wifi-icon offline-banner__wifi-icon--offline" />
          </div>

          <!-- Текст -->
          <div class="offline-banner__text">
            <span v-if="isReconnecting">Подключение...</span>
            <span v-else>Нет подключения к интернету</span>
          </div>

          <!-- Кнопка закрыть -->
          <button
            v-if="!isReconnecting"
            class="offline-banner__close"
            aria-label="Закрыть"
            @click="dismiss"
          >
            <Icon name="heroicons:x-mark" />
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { consola } from 'consola'
import { offlineManager, OfflineState } from '~/shared/lib/auth'

const logger = consola.withTag('OfflineBanner')

// Состояние
const isOffline = ref(false)
const isReconnecting = ref(false)
const isDismissed = ref(false)

/**
 * Скрыть баннер вручную
 */
const dismiss = () => {
  isDismissed.value = true
  isOffline.value = false
}

/**
 * Обработка изменения состояния offline
 */
const handleStateChange = (state: OfflineState) => {
  isReconnecting.value = state === OfflineState.RECONNECTING

  if (state === OfflineState.OFFLINE && !isDismissed.value) {
    isOffline.value = true
  } else if (state === OfflineState.ONLINE) {
    isOffline.value = false
    isDismissed.value = false
  }
}

/**
 * Обработка offline события
 */
const handleOffline = () => {
  if (!isDismissed.value) {
    isOffline.value = true
  }
}

/**
 * Обработка online события
 */
const handleOnline = () => {
  isOffline.value = false
  isDismissed.value = false
}

// Lifecycle hooks
onMounted(() => {
  // Подписываемся на события offlineManager
  offlineManager.on('stateChange', handleStateChange)
  offlineManager.on('offline', handleOffline)
  offlineManager.on('online', handleOnline)

  // Проверяем начальное состояние
  const currentState = offlineManager.getState()
  if (currentState === OfflineState.OFFLINE) {
    isOffline.value = true
  } else if (currentState === OfflineState.RECONNECTING) {
    isOffline.value = true
    isReconnecting.value = true
  }

  logger.log('[OfflineBanner] Mounted', {
    initialState: currentState,
    isOnline: offlineManager.isOnline(),
  })
})

onBeforeUnmount(() => {
  logger.log('[OfflineBanner] Unmounted')
})
</script>

<style lang="scss" scoped>
.offline-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9998;
  pointer-events: all;

  // Safe area для iOS
  padding-top: env(safe-area-inset-top, 0);

  @apply bg-red-500 dark:bg-red-600;
  @apply shadow-lg;
}

.offline-banner__container {
  @apply max-w-7xl mx-auto px-4 sm:px-6 lg:px-8;
}

.offline-banner__content {
  @apply flex items-center justify-between gap-3 py-2;
  @apply text-white;
}

.offline-banner__icon {
  @apply flex-shrink-0 w-5 h-5;
}

.offline-banner__wifi-icon {
  @apply w-5 h-5;

  &--offline {
    animation: wifi-pulse 2s ease-in-out infinite;
  }
}

.offline-banner__spinner {
  @apply text-white;
  animation: spinner-rotate 1s linear infinite;

  circle {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: 0;
    stroke-linecap: round;
    animation: spinner-dash 1.5s ease-in-out infinite;
  }
}

@keyframes spinner-rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes spinner-dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}

@keyframes wifi-pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.offline-banner__text {
  @apply flex-1 text-sm font-medium;
  @apply text-center sm:text-left;
}

.offline-banner__close {
  @apply flex-shrink-0 w-5 h-5;
  @apply text-white hover:text-red-100;
  @apply transition-colors duration-200;
  @apply focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-red-500;
  @apply rounded;
}

// Transitions
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease-out;
}

.slide-down-enter-from {
  transform: translateY(-100%);
  opacity: 0;
}

.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>
